import { Modal } from '@/components/common/dialog';
import { ModalButton } from '@/components/common/modal-button';
import { MarkerIcon } from '@/components/icons';
import { useConfigStore } from '@/stores';
import { generateFloorName } from '@/utils/floor-guide';
import type { IDataFloorName } from 'dabeeomaps/dist/src/model/map/IDataFloorName';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    isOpen?: boolean;
    category?: string;
    building?: string;
    floorName?: string | IDataFloorName[];
    onGoToFloor?: () => void;
    onClose?: () => void;
};

const resolveFloorNameText = (floorName?: string | IDataFloorName[]) => {
    if (typeof floorName === 'string') {
        return floorName;
    }
    if (!Array.isArray(floorName) || !floorName.length) {
        return '';
    }
    return floorName[0]?.text ?? '';
};

export const NoCategoryPoiPopup = ({ category, building, floorName, isOpen = false, onGoToFloor, onClose }: Props) => {
    const { t } = useTranslation();
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);

    const isShowBuildingName = useMemo(() => {
        return Array.isArray(buildings) && buildings.length > 1;
    }, [buildings]);

    const displayFloorName = useMemo(() => resolveFloorNameText(floorName), [floorName]);

    const generateArialLabel = useMemo(() => {
        return `${category}, ${t('common.tts.location')}, ${isShowBuildingName ? `${building}, ` : ''} ${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(displayFloorName) })}`;
    }, [category, isShowBuildingName, building, displayFloorName, t]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => onClose?.()}
            className="max-w-[calc(680*var(--vw-unit))] pt-[calc(56*var(--vh-unit))] pb-[calc(52*var(--vh-unit))]"
        >
            <section className="text-text-main01 text-center">
                <p
                    data-modal-region
                    className="focus-border mx-[calc(10*var(--vw-unit))] text-[calc(32*var(--vh-unit))] font-bold"
                    tabIndex={0}
                    data-interactive
                    aria-label={t('floor_guide.title.location_of_convenience_facilities')}
                >
                    {t('floor_guide.title.location_of_convenience_facilities')}
                </p>
                <p
                    className="focus-border mx-[calc(10*var(--vw-unit))] text-[calc(22*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-medium whitespace-pre-wrap"
                    tabIndex={0}
                    data-interactive
                    aria-label={t('floor_guide.message.go_to_the_nearest_floor_where_the_facilities_is_located')}
                >
                    {t('floor_guide.message.go_to_the_nearest_floor_where_the_facilities_is_located')}
                </p>
            </section>
            <section
                className="focus-border bg-not-contrast-bg-gray-f4 mx-auto mt-[calc(24*var(--vh-unit))] flex w-[calc(440*var(--vw-unit))] flex-col items-center gap-[calc(8*var(--vh-unit))] rounded-md py-[calc(24*var(--vh-unit))]"
                data-interactive
                tabIndex={0}
                aria-label={generateArialLabel}
            >
                <p className="text-not-contrast-dark-393734 focus-border text-[calc(28*var(--vh-unit))] font-bold">{category}</p>
                <section className="flex items-center justify-center gap-[calc(8*var(--vh-unit))]">
                    <div className="text-not-contrast-gray-67615d flex items-center justify-center">
                        <MarkerIcon width={24} height={24} />
                    </div>
                    <div className="flex items-center justify-center gap-[calc(12*var(--vw-unit))] text-[calc(20*var(--vh-unit))] leading-[calc(30*var(--vh-unit))]">
                        {isShowBuildingName && (
                            <>
                                <p className="text-not-contrast-gray-67615d focus-border font-normal">{building}</p>
                                <div className="bg-not-contrast-gray-99938f h-[calc(20*var(--vh-unit))] w-[calc(2*var(--vw-unit))]" />
                            </>
                        )}
                        <p className="text-not-contrast-dark-393734 number focus-border font-bold">{displayFloorName}</p>
                    </div>
                </section>
            </section>
            <section className="mt-[calc(64*var(--vh-unit))] flex items-center justify-center gap-[calc(23*var(--vw-unit))]">
                <ModalButton
                    tabIndex={0}
                    data-interactive
                    onClick={() => onClose?.()}
                    aria-label={t('floor_guide.button.cancel')}
                    type="button"
                    data-role={t('common.data_role.button')}
                >
                    {t('floor_guide.button.cancel')}
                </ModalButton>
                <ModalButton
                    tabIndex={0}
                    data-interactive
                    variant="secondary"
                    onClick={() => onGoToFloor?.()}
                    aria-label={t('floor_guide.button.check')}
                    type="button"
                    data-role={t('common.data_role.button')}
                >
                    {t('floor_guide.button.check')}
                </ModalButton>
            </section>
        </Modal>
    );
};
